import { connect, Contract, keyStores, WalletConnection, utils } from 'near-api-js';

const nearEnv = import.meta.env.VITE_NEAR_NETWORK || 'mainnet';

const baseConfig = {
  testnet: {
    networkId: 'testnet',
    nodeUrl: 'https://rpc.testnet.near.org',
    walletUrl: 'https://testnet.mynearwallet.com',
    helperUrl: 'https://helper.testnet.near.org',
    explorerUrl: 'https://explorer.testnet.near.org',
  },
  mainnet: {
    networkId: 'mainnet',
    nodeUrl: 'https://rpc.mainnet.pagoda.co',
    walletUrl: 'https://app.mynearwallet.com',
    helperUrl: 'https://helper.mainnet.near.org',
    explorerUrl: 'https://explorer.near.org',
  },
};

const rpcEndpointCandidates = {
  testnet: [
    'https://rpc.testnet.pagoda.co',
    'https://rpc.testnet.near.org',
    'https://rpc.fastnear.com/testnet',
    'https://rpc.ankr.com/near_testnet',
    'https://near-testnet.lava.build',
    'https://near-testnet.blockpi.network/v1/rpc/public',
  ],
  mainnet: [
    'https://rpc.mainnet.pagoda.co',
    'https://rpc.mainnet.near.org',
    'https://rpc.fastnear.com',
    'https://rpc.ankr.com/near',
    'https://near-mainnet.lava.build',
    'https://near-mainnet.api.blockpi.io/v1/rpc/public',
  ],
};

const normalizeUrl = (url) => {
  if (!url) return null;
  return url.replace(/\/$/, '');
};

const getNodeUrlCandidates = () => {
  const defaultUrl = normalizeUrl(baseConfig[nearEnv]?.nodeUrl);
  const isBrowser = typeof window !== 'undefined';
  const origin = isBrowser ? normalizeUrl(window.location.origin || '') : null;

  const candidates = [];
  const addCandidate = (value) => {
    const normalized = normalizeUrl(value);
    if (normalized && normalized.startsWith('http') && !candidates.includes(normalized)) {
      candidates.push(normalized);
    }
  };

  let envUrl = normalizeUrl(import.meta.env.VITE_NEAR_RPC_URL);

  if (envUrl) {
    if (!envUrl.startsWith('http') && origin) {
      const separator = envUrl.startsWith('/') ? '' : '/';
      envUrl = normalizeUrl(`${origin}${separator}${envUrl}`);
    }
    addCandidate(envUrl);
  }

  if (isBrowser && origin) {
    addCandidate(`${origin}/near-rpc`);
  }

  if (import.meta.env.DEV) {
    addCandidate(import.meta.env.VITE_NEAR_LOCAL_PROXY || 'http://localhost:8787');
  }

  (rpcEndpointCandidates[nearEnv] || []).forEach(addCandidate);

  addCandidate(defaultUrl);

  return candidates.length > 0 ? candidates : [defaultUrl].filter(Boolean);
};

const testRpcEndpoint = async (url) => {
  if (typeof window !== 'undefined') {
    const origin = window.location.origin;
    if (url.startsWith(origin) || url.startsWith('http://localhost') || url.startsWith('https://localhost')) {
      return;
    }
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        jsonrpc: '2.0',
        id: 'healthcheck',
        method: 'status',
        params: null,
      }),
      mode: 'cors',
      signal: controller.signal,
    });

    if (!response.ok) {
      throw new Error(`RPC responded with ${response.status}`);
    }

    await response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      throw new Error('RPC healthcheck timed out');
    }

    if (error instanceof TypeError) {
      throw new Error('RPC healthcheck failed (network or CORS issue)');
    }

    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

// Initialize the connection to the configured NEAR network
export const initializeNear = async () => {
  const keyStore = new keyStores.BrowserLocalStorageKeyStore();
  const networkConfig = {
    ...baseConfig[nearEnv],
  };

  let lastError = null;
  const candidates = getNodeUrlCandidates();

  for (const nodeUrl of candidates) {
    if (!nodeUrl.startsWith('http')) {
      continue;
    }

    try {
      await testRpcEndpoint(nodeUrl);

      const nearConnection = await connect({
        ...networkConfig,
        nodeUrl,
        keyStore,
        headers: {},
      });

      nearConnection.config.nodeUrl = nodeUrl;

      const walletConnection = new WalletConnection(nearConnection, 'nearbets');
      if (typeof window !== 'undefined') {
        try {
          window.sessionStorage.setItem('nearbets:lastRpc', nodeUrl);
        } catch (storageError) {
          console.debug('[NearBets] Unable to persist RPC selection', storageError);
        }
      }
      if (typeof window !== 'undefined') {
        console.info(`[NearBets] Connected to NEAR RPC: ${nodeUrl}`);
      }
      return { nearConnection, walletConnection, nodeUrl };
    } catch (error) {
      console.warn(`[NearBets] Failed to initialize NEAR via ${nodeUrl}`, error);
      lastError = error;
    }
  }

  if (typeof window !== 'undefined') {
    try {
      const cached = window.sessionStorage.getItem('nearbets:lastRpc');
      if (cached && cached.startsWith('http')) {
        console.warn('[NearBets] Retrying cached RPC endpoint after initial failures', cached);
        return initializeNearWithFallback(networkConfig, cached, keyStore);
      }
    } catch (storageError) {
      console.debug('[NearBets] Unable to read cached RPC selection', storageError);
    }
  }

  throw lastError || new Error('Unable to initialize NEAR with any RPC endpoint.');
};

async function initializeNearWithFallback(networkConfig, nodeUrl, keyStore) {
  const nearConnection = await connect({
    ...networkConfig,
    nodeUrl,
    keyStore,
    headers: {},
  });

  nearConnection.config.nodeUrl = nodeUrl;
  const walletConnection = new WalletConnection(nearConnection, 'nearbets');
  return { nearConnection, walletConnection, nodeUrl };
}

// Contract interface
export class NearBetsContract {
  constructor(walletConnection, contractId) {
    this.walletConnection = walletConnection;
    this.contractId = contractId;
    this.contract = new Contract(
      walletConnection.account(),
      contractId,
      {
        viewMethods: [
          'get_wager',
          'get_active_wagers',
          'get_user_stats',
          'get_platform_stats',
        ],
        changeMethods: [
          'create_wager',
          'join_wager',
          'resolve_wager',
          'claim_winnings',
        ],
      }
    );
  }

  // View methods
  async getWager(wagerId) {
    return await this.contract.get_wager({ wager_id: wagerId });
  }

  async getActiveWagers(from = 0, limit = 10) {
    return await this.contract.get_active_wagers({ from, limit });
  }

  async getUserWagers(accountId, from = 0, limit = 100, includeResolved = true) {
    return await this.contract.get_user_wagers({ 
      account_id: accountId, 
      from, 
      limit, 
      include_resolved: includeResolved 
    });
  }

  async getUserStats(accountId) {
    return await this.contract.get_user_stats({ account_id: accountId });
  }

  async getPlatformStats() {
    return await this.contract.get_platform_stats();
  }

  // Transaction methods
  async createWager(wagerRequest, initialChoice, deposit) {
    const depositAmount = utils.format.parseNearAmount(deposit.toString());
    
    return await this.contract.create_wager(
      {
        request: wagerRequest,
        initial_choice: initialChoice,
      },
      '300000000000000', // 300 TGas
      depositAmount
    );
  }

  async joinWager(wagerId, choice, deposit) {
    const depositAmount = utils.format.parseNearAmount(deposit.toString());
    
    return await this.contract.join_wager(
      {
        wager_id: wagerId,
        choice: choice,
      },
      '300000000000000', // 300 TGas
      depositAmount
    );
  }

  async resolveWager(wagerId, outcome) {
    return await this.contract.resolve_wager(
      {
        wager_id: wagerId,
        outcome: outcome,
      },
      '200000000000000' // 200 TGas
    );
  }

  async claimWinnings(wagerId) {
    return await this.contract.claim_winnings(
      {
        wager_id: wagerId,
      },
      '200000000000000' // 200 TGas
    );
  }
}

// NEAR service class
export class NearService {
  constructor() {
    this.walletConnection = null;
    this.contract = null;
    this.contractId = import.meta.env.VITE_CONTRACT_NAME || 'nearbets.near';
    this.initializingPromise = null;
  }

  async initialize() {
    if (this.walletConnection) {
      return this.walletConnection.isSignedIn();
    }

    if (this.initializingPromise) {
      await this.initializingPromise;
      return this.walletConnection?.isSignedIn() ?? false;
    }

    this.initializingPromise = (async () => {
      const { walletConnection } = await initializeNear();
      this.walletConnection = walletConnection;
      this.contract = new NearBetsContract(walletConnection, this.contractId);
      return this.walletConnection.isSignedIn();
    })();

    try {
      return await this.initializingPromise;
    } finally {
      this.initializingPromise = null;
    }
  }

  // Wallet methods
  signIn() {
    this.walletConnection.requestSignIn({
      contractId: this.contractId,
      methodNames: [
        'create_wager',
        'join_wager',
        'resolve_wager',
        'claim_winnings',
      ],
    });
  }

  async getUserStats(accountId) {
    return await this.contract.getUserStats(accountId);
  }

  signOut() {
    // Clear wallet connection
    this.walletConnection.signOut();
    
    // Clear any cached state
    this.contract = null;
    
    // Force a clean redirect to home page to ensure fresh state
    window.location.href = window.location.origin;
  }

  isSignedIn() {
    return this.walletConnection?.isSignedIn() || false;
  }

  getAccountId() {
    return this.walletConnection?.getAccountId() || null;
  }

  async getAccountBalance() {
    if (!this.isSignedIn()) return '0';
    
    const account = this.walletConnection.account();
    const balance = await account.getAccountBalance();
    return utils.format.formatNearAmount(balance.available);
  }

  // Utility methods
  formatNearAmount(amount) {
    return utils.format.formatNearAmount(amount.toString());
  }

  parseNearAmount(amount) {
    return utils.format.parseNearAmount(amount.toString());
  }

  // Transaction status checking
  async getTransactionStatus(txHash) {
    try {
      const result = await this.walletConnection.account().connection.provider.txStatus(
        txHash,
        this.getAccountId()
      );
      return result;
    } catch (error) {
      console.error('Error getting transaction status:', error);
      return null;
    }
  }
}

// Create singleton instance
export const nearService = new NearService();

// Hook for React components
export const useNear = () => {
  return nearService;
};

export default nearService;
